import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import '../MandantenList.css';
import { MandantData } from '../MandantenList';
import EinladenDialog, { registrierLink } from './EinladenDialog';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface BenutzerRow {
  benutzer_id: number;
  name: string;
  email: string;
  rolle: string;
  aktiv: boolean;
  erstellt_am: string;
  tenant_id: number | null;
  tenant_slug: string | null;
  einladung_offen: boolean;
}

interface SuperAdminBenutzerListProps {
  authToken: string;
}

const SuperAdminBenutzerList: React.FC<SuperAdminBenutzerListProps> = ({ authToken }) => {
  const [benutzer, setBenutzer] = useState<BenutzerRow[]>([]);
  const [mandanten, setMandanten] = useState<MandantData[]>([]);
  const [filterTenant, setFilterTenant] = useState<string>('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [dialogOffen, setDialogOffen] = useState(false);
  const [neuerLink, setNeuerLink] = useState<{ benutzerId: number; link: string; versendet: boolean } | null>(null);

  const headers = { Authorization: `Bearer ${authToken}` };

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      const params = filterTenant ? `?tenant_id=${filterTenant}` : '';
      const [b, m] = await Promise.all([
        axios.get(`${API_URL}/api/superadmin/benutzer${params}`, { headers }),
        axios.get(`${API_URL}/api/mandanten`, { headers }),
      ]);
      setBenutzer(b.data);
      setMandanten(m.data);
    } catch (e: any) {
      setFehler(e.response?.data?.detail || 'Benutzer konnten nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, filterTenant]);

  useEffect(() => { laden(); }, [laden]);

  const toggleAktiv = async (b: BenutzerRow) => {
    const aktion = b.aktiv ? 'deaktivieren' : 'aktivieren';
    try {
      await axios.put(`${API_URL}/api/benutzer/${b.benutzer_id}/${aktion}`, {}, { headers });
      laden();
    } catch (e: any) {
      setFehler(e.response?.data?.detail || `Konnte Benutzer nicht ${aktion}.`);
    }
  };

  const neuerEinladungsLink = async (b: BenutzerRow) => {
    try {
      const r = await axios.post(`${API_URL}/api/benutzer/${b.benutzer_id}/einladung`, {}, { headers });
      setNeuerLink({ benutzerId: b.benutzer_id, link: registrierLink(r.data.einladungs_token), versendet: !!r.data.email_versendet });
      laden();
    } catch (e: any) {
      setFehler(e.response?.data?.detail || 'Link konnte nicht erzeugt werden.');
    }
  };

  const statusLabel = (b: BenutzerRow) =>
    !b.aktiv ? 'inaktiv' : b.einladung_offen ? 'eingeladen' : 'aktiv';

  return (
    <div className="mandanten-list-container">
      <div className="mandanten-list-header">
        <div>
          <div className="mandanten-list-title">Benutzer</div>
          <div className="mandanten-list-subtitle">alle Mandanten dieser Instanz</div>
        </div>
        <button className="btn-edit" onClick={() => setDialogOffen(true)}>
          + Benutzer einladen
        </button>
      </div>

      <div className="mandanten-filter-row">
        <select
          className="mandanten-search-input"
          value={filterTenant}
          onChange={e => setFilterTenant(e.target.value)}
        >
          <option value="">Alle Mandanten</option>
          {mandanten.map(m => (
            <option key={m.mandant_id} value={m.mandant_id}>{m.firmenname}</option>
          ))}
        </select>
      </div>

      {fehler && <div className="mandanten-error">{fehler}</div>}

      {laedt ? (
        <div className="mandanten-loading"><span className="spinner" /> Lade Benutzer …</div>
      ) : benutzer.length === 0 ? (
        <div className="mandanten-empty">Keine Benutzer gefunden.</div>
      ) : (
        <table className="mandanten-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>E-Mail</th>
              <th>Rolle</th>
              <th>Mandant</th>
              <th>Status</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {benutzer.map(b => (
              <React.Fragment key={b.benutzer_id}>
                <tr>
                  <td className="mandant-firmenname">{b.name}</td>
                  <td>{b.email}</td>
                  <td>{b.rolle}</td>
                  <td className="mandant-slug">{b.tenant_slug ?? '—'}</td>
                  <td>{statusLabel(b)}</td>
                  <td className="mandanten-actions">
                    <button className="btn-edit" onClick={() => neuerEinladungsLink(b)}
                            title="Neuen Einladungs-/Passwort-Reset-Link erzeugen">
                      Neuer Link
                    </button>
                    <button className="btn-demo" onClick={() => toggleAktiv(b)}>
                      {b.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                    </button>
                  </td>
                </tr>
                {neuerLink?.benutzerId === b.benutzer_id && (
                  <tr>
                    <td colSpan={6}>
                      <p style={{ margin: '0 0 6px' }}>
                        {neuerLink.versendet ? 'Link per E-Mail verschickt (72 Stunden gültig).' : 'Mailversand nicht eingerichtet – Link manuell weitergeben:'}
                      </p>
                      <input
                        type="text"
                        readOnly
                        value={neuerLink.link}
                        style={{ width: '100%' }}
                        onFocus={e => e.target.select()}
                      />
                      <button
                        className="btn-edit"
                        onClick={() => navigator.clipboard.writeText(neuerLink.link)}
                      >
                        Link kopieren
                      </button>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {dialogOffen && (
        <EinladenDialog
          authToken={authToken}
          mandanten={mandanten}
          onClose={() => setDialogOffen(false)}
          onDone={laden}
        />
      )}
    </div>
  );
};

export default SuperAdminBenutzerList;
